import { create } from 'zustand';

const STORAGE_KEY = 'usagi_shop_purchases';

interface ShopPurchasesState {
  /**
   * Set of `spellId`s whose grimoire has already been bought from a shop
   * (any shop, any time). Rule: one spell = a single grimoire purchase across
   * the whole save. Independent of:
   * - What the player did with the grimoire after buying (used, dropped, sold).
   * - Whether the player obtained the grimoire from other sources (mob drop,
   *   location).
   *
   * Concretely: a looted grimoire stays usable and unlocks the spell; but once
   * the spell has been bought from a shop, no shop will sell it again (all
   * tracked on this global list).
   */
  purchasedSpellUnlocks: string[];
}

interface ShopPurchasesActions {
  /** Marks the spell as already bought via grimoire (any shop). Idempotent. */
  recordSpellUnlockPurchase: (spellId: string) => void;
  /** True if a grimoire unlocking this spell has already been bought. */
  hasSpellUnlockBeenPurchased: (spellId: string) => boolean;
  resetShopPurchases: () => void;
}

function load(): ShopPurchasesState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ShopPurchasesState>;
      return { purchasedSpellUnlocks: parsed.purchasedSpellUnlocks ?? [] };
    }
  } catch {
    /* ignore */
  }
  return { purchasedSpellUnlocks: [] };
}

function persist(state: ShopPurchasesState): void {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ purchasedSpellUnlocks: state.purchasedSpellUnlocks }),
  );
}

export const useShopPurchasesStore = create<
  ShopPurchasesState & ShopPurchasesActions
>((set, get) => ({
  ...load(),

  recordSpellUnlockPurchase: (spellId) => {
    set((s) => {
      if (s.purchasedSpellUnlocks.includes(spellId)) {
        return s;
      }
      const next = {
        purchasedSpellUnlocks: [...s.purchasedSpellUnlocks, spellId],
      };
      persist(next);
      return next;
    });
  },

  hasSpellUnlockBeenPurchased: (spellId) => {
    return get().purchasedSpellUnlocks.includes(spellId);
  },

  resetShopPurchases: () => {
    const reset = { purchasedSpellUnlocks: [] };
    set(reset);
    persist(reset);
  },
}));
